import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, GraduationCap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/Field";
import { TwoFactorStep } from "@/components/TwoFactorStep";
import { GoogleButton, useGoogleReturn } from "@/components/GoogleSignIn";
import { ChangePassword } from "@/components/ChangePassword";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { errText } from "@/lib/aadhaar";

export default function StudentLogin() {
  const { L } = useLang();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [mustChange, setMustChange] = useState(new URLSearchParams(window.location.search).get("change") === "1" && !!localStorage.getItem("rs_student_token"));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const next = new URLSearchParams(window.location.search).get("next") || "";
  const safeNext = /^\/(?![/\\])/.test(next) ? next : "/student";
  const finish = (data) => {
    localStorage.setItem("rs_student_token", data.token);
    if (data.user?.must_change_password) { setMfa(null); setMustChange(true); return; }
    nav(safeNext);
  };
  const gBusy = useGoogleReturn((data) => (data.mfa_required ? setMfa(data) : finish(data)), setErr);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = mode === "login" ? await api.post("/auth/login", { email: f.email, password: f.password })
        : await api.post("/auth/student/register", f);
      if ((data.mfa_required ? data.role : data.user?.role) !== "student") { setErr(L("This is not a student account. Please use the correct sign-in page.", "यह छात्र खाता नहीं है।")); return; }
      setMfa(data);
    } catch (e2) {
      setErr(errText(e2.response?.data?.detail, L("Sign in failed", "साइन इन नहीं हो सका")));
    } finally { setLoading(false); }
  };

  const tab = (k, label) => (
    <button type="button" onClick={() => { setMode(k); setErr(""); }} data-testid={`student-tab-${k}`}
      className={`flex-1 h-10 rounded-full text-sm font-bold transition-colors ${mode === k ? "bg-[#FF3B3B] text-white" : "text-slate-600 hover:text-[#0A192F]"}`}>{label}</button>
  );

  return (
    <div className="min-h-[80vh] rs-radial grid place-items-center px-5 py-14" data-testid="student-login-page">
      <div className="bg-white/90 backdrop-blur-xl border border-slate-200 rounded-3xl p-7 sm:p-8 w-full max-w-md shadow-[0_40px_120px_-30px_rgba(255,46,99,0.45)]">
        {mustChange ? <ChangePassword token={localStorage.getItem("rs_student_token")} onDone={() => nav(safeNext)} /> : mfa ? <TwoFactorStep mfa={mfa} onSuccess={finish} /> : gBusy ? (
          <p className="flex items-center justify-center gap-2 py-10 text-sm font-bold text-slate-600" data-testid="student-google-wait"><Loader2 className="w-4 h-4 animate-spin" />{L("Signing you in with Google…", "Google से साइन इन हो रहा है…")}</p>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="student-auth-form">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#F0F4F8] grid place-items-center"><GraduationCap className="w-5 h-5 text-[#007A8A]" /></div>
              <div>
                <h1 className="text-2xl font-extrabold text-[#0A192F]" data-testid="student-login-title">{L("Student portal", "छात्र पोर्टल")}</h1>
                <p className="text-xs text-slate-500">{L("Admission, ID card, admit cards and Zoya Ma'am's classroom", "दाखिला, ID कार्ड, एडमिट कार्ड और ज़ोया मैम की क्लास")}</p>
              </div>
            </div>
            <div className="flex gap-1 p-1 rounded-full bg-[#F0F4F8] border border-slate-200">{tab("login", L("Sign in", "साइन इन"))}{tab("register", L("Create account", "खाता बनाएँ"))}</div>
            <GoogleButton label={mode === "login" ? L("Continue with Google", "Google से जारी रखें") : L("Sign up with Google", "Google से खाता बनाएँ")} />
            <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-slate-200" />{L("or with email", "या ईमेल से")}<span className="h-px flex-1 bg-slate-200" /></div>
            {mode === "register" && <Field id="student_name" label={L("Student's full name", "छात्र का पूरा नाम")} required value={f.name} onChange={set("name")} />}
            <Field id="student_email" label={mode === "login" ? L("Email or Login ID", "ईमेल या लॉगिन ID") : L("Email", "ईमेल")} type={mode === "login" ? "text" : "email"} required value={f.email} onChange={set("email")} />
            <Field id="student_password" label={L("Password", "पासवर्ड")} type="password" required minLength={mode === "register" ? 8 : undefined} value={f.password} onChange={set("password")} />
            {mode === "register" && <p className="text-[11px] text-slate-500">{L("At least 8 characters.", "कम से कम 8 अक्षर।")}</p>}
            {err && <p className="text-sm text-red-600" data-testid="student-auth-error">{err}</p>}
            <Button type="submit" disabled={loading} className="w-full rs-cta h-11 rounded-full" data-testid="student-auth-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? L("Sign in", "साइन इन") : L("Create account", "खाता बनाएँ")}
            </Button>
            <p className="flex items-center gap-2 text-[11px] text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />{L("Protected by two-step verification: email code or authenticator app.", "दो-चरण सत्यापन से सुरक्षित: ईमेल कोड या authenticator ऐप।")}</p>
            <p className="text-xs text-slate-500">{L("Parent? Track progress in the", "अभिभावक? प्रगति देखें")} <Link to="/progress" className="inline-block py-2 text-[#007A8A] hover:underline" data-testid="student-parent-portal-link">{L("Parent Portal", "पैरेंट पोर्टल")}</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}
